/**
 * InteractiveChart - Robinhood-style chart with scrub gestures
 *
 * Features:
 * - Touch/mouse drag to scrub through data
 * - Gradient fill based on performance
 * - Smooth animations
 * - Timeframe selector
 * - Haptic feedback on scrub
 */

import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useVault } from '../../contexts/VaultContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { VaultTimeframe } from '../../services/api';

interface InteractiveChartProps {
  className?: string;
  height?: number;
}

const TIMEFRAMES: { value: VaultTimeframe; label: string }[] = [
  { value: '1D', label: '1D' },
  { value: '1W', label: '1W' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '1Y', label: '1Y' },
  { value: 'ALL', label: 'ALL' },
];

const InteractiveChart: React.FC<InteractiveChartProps> = ({
  className = '',
  height = 250,
}) => {
  const {
    chartData,
    selectedTimeframe,
    setTimeframe,
    startScrub,
    updateScrub,
    endScrub,
    isScrubbing,
    scrubIndex,
    isChartLoading,
    chartChangePercent,
  } = useVault();

  const { feedback, triggerHaptic } = useFeedback();

  const chartRef = useRef<HTMLDivElement>(null);
  const lastScrubIndexRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Get current chart data
  const currentData = useMemo(() => {
    return chartData.get(selectedTimeframe) || [];
  }, [chartData, selectedTimeframe]);

  // Calculate chart bounds
  const { minValue, maxValue, startValue } = useMemo(() => {
    if (currentData.length === 0) {
      return { minValue: 0, maxValue: 100, startValue: 0 };
    }

    const values = currentData.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1;

    return {
      minValue: min - padding,
      maxValue: max + padding,
      startValue: values[0],
    };
  }, [currentData]);

  // Determine if chart shows positive or negative performance
  const isPositive = chartChangePercent >= 0;

  // Chart gradient colors
  const gradientColor = isPositive ? '#22c55e' : '#ef4444';
  const gradientId = `chartGradient-${selectedTimeframe}`;

  // Handle scrub position calculation
  const calculateScrubIndex = useCallback((clientX: number): number | null => {
    if (!chartRef.current || currentData.length === 0) return null;

    const rect = chartRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const index = Math.round(percentage * (currentData.length - 1));

    return index;
  }, [currentData.length]);

  // Handle mouse/touch events
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startScrub();

    const index = calculateScrubIndex(e.clientX);
    if (index !== null) {
      updateScrub(index);
      lastScrubIndexRef.current = index;
      triggerHaptic('light');
    }
  }, [startScrub, calculateScrubIndex, updateScrub, triggerHaptic]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    const index = calculateScrubIndex(e.clientX);
    if (index !== null && index !== lastScrubIndexRef.current) {
      updateScrub(index);
      lastScrubIndexRef.current = index;
      triggerHaptic('light');
    }
  }, [isDragging, calculateScrubIndex, updateScrub, triggerHaptic]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    endScrub();
    lastScrubIndexRef.current = null;
  }, [endScrub]);

  // Handle pointer leave
  const handlePointerLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      endScrub();
      lastScrubIndexRef.current = null;
    }
  }, [isDragging, endScrub]);

  // Global pointer up handler
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      if (isDragging) {
        setIsDragging(false);
        endScrub();
        lastScrubIndexRef.current = null;
      }
    };

    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => window.removeEventListener('pointerup', handleGlobalPointerUp);
  }, [isDragging, endScrub]);

  // Handle timeframe change
  const handleTimeframeChange = useCallback((timeframe: VaultTimeframe) => {
    feedback('pop', 'selection');
    setTimeframe(timeframe);
  }, [feedback, setTimeframe]);

  // Calculate scrub line position
  const scrubLinePosition = useMemo(() => {
    if (scrubIndex === null || currentData.length === 0) return null;
    return (scrubIndex / (currentData.length - 1)) * 100;
  }, [scrubIndex, currentData.length]);

  // Loading skeleton
  if (isChartLoading && currentData.length === 0) {
    return (
      <div className={`${className} animate-pulse`}>
        <div className="h-64 bg-gray-800 rounded-lg" />
        <div className="flex justify-center gap-2 mt-4">
          {TIMEFRAMES.map(tf => (
            <div key={tf.value} className="w-12 h-8 bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Chart container */}
      <div
        ref={chartRef}
        className="relative touch-none select-none cursor-crosshair"
        style={{ height }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
      >
        {/* Recharts Area Chart */}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={currentData}
            margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={gradientColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={gradientColor} stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis dataKey="timestamp" hide />
            <YAxis domain={[minValue, maxValue]} hide />

            {/* Reference line at starting value */}
            <ReferenceLine
              y={startValue}
              stroke="#374151"
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke={gradientColor}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              isAnimationActive={!isScrubbing}
              animationDuration={500}
            />
          </AreaChart>
        </ResponsiveContainer>

        {/* Scrub line indicator */}
        <AnimatePresence>
          {isScrubbing && scrubLinePosition !== null && (
            <motion.div
              className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
              style={{ left: `${scrubLinePosition}%` }}
              initial={{ opacity: 0, scaleY: 0.8 }}
              animate={{ opacity: 1, scaleY: 1 }}
              exit={{ opacity: 0, scaleY: 0.8 }}
              transition={{ duration: 0.1 }}
            >
              {/* Scrub dot */}
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading overlay */}
        <AnimatePresence>
          {isChartLoading && (
            <motion.div
              className="absolute inset-0 bg-gray-900/50 flex items-center justify-center rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="w-8 h-8 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Timeframe selector */}
      <div className="flex justify-center gap-1 mt-4">
        {TIMEFRAMES.map(tf => (
          <motion.button
            key={tf.value}
            onClick={() => handleTimeframeChange(tf.value)}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${selectedTimeframe === tf.value
                ? 'bg-white text-black'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }
            `}
            whileTap={{ scale: 0.95 }}
          >
            {tf.label}
          </motion.button>
        ))}
      </div>

      {/* Hint text */}
      <AnimatePresence>
        {!isScrubbing && (
          <motion.p
            className="text-center text-gray-500 text-sm mt-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Drag across the chart to explore
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveChart;
